import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { registerLogin } from '../api/license.api';
import LicenseKeyInput from '../components/login/LicenseKeyInput';
import PasswordRecoveryModal from '../components/login/PasswordRecoveryModal';
import styles from './LoginPage.module.css';

type TabType = 'credentials' | 'licenseKey';

export default function LoginPage() {
  const { login, loginWithLicenseKey } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('credentials');

  // 아이디/비밀번호 로그인 state
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');

  // 라이선스 키 로그인 state
  const [licenseKey, setLicenseKey] = useState('');

  // 계정 설정 모달 state
  const [showRegister, setShowRegister] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [regLicenseKey, setRegLicenseKey] = useState('');
  const [regLoginId, setRegLoginId] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [regMessage, setRegMessage] = useState<string | null>(null);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginId.trim() || !password.trim()) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await login(loginId.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLicenseKeySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = licenseKey.trim();
    if (!trimmed) {
      setError('라이선스 키를 입력해주세요.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await loginWithLicenseKey(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegMessage(null);
    setError(null);

    if (!regLicenseKey.trim()) {
      setError('라이선스 키를 입력해주세요.');
      return;
    }
    if (regLoginId.trim().length < 4) {
      setError('아이디는 4자 이상이어야 합니다.');
      return;
    }
    if (regPassword.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (regPassword !== regPasswordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsSubmitting(true);
    try {
      await registerLogin(regLicenseKey.trim(), regLoginId.trim(), regPassword);
      setRegMessage('계정이 설정되었습니다. 아이디/비밀번호로 로그인하세요.');
      setShowRegister(false);
      setActiveTab('credentials');
      setLoginId(regLoginId.trim());
      setRegLicenseKey('');
      setRegLoginId('');
      setRegPassword('');
      setRegPasswordConfirm('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '계정 설정에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>대기 관리</h1>
          <p className={styles.subtitle}>업체용 관리 시스템</p>
        </div>

        {!showRegister ? (
          <>
            {/* 탭 */}
            <div className={styles.tabs}>
              <button
                type="button"
                className={`${styles.tab} ${activeTab === 'credentials' ? styles.tabActive : ''}`}
                onClick={() => { setActiveTab('credentials'); setError(null); }}
              >
                아이디/비밀번호
              </button>
              <button
                type="button"
                className={`${styles.tab} ${activeTab === 'licenseKey' ? styles.tabActive : ''}`}
                onClick={() => { setActiveTab('licenseKey'); setError(null); }}
              >
                라이선스 키
              </button>
            </div>

            {activeTab === 'credentials' ? (
              <form className={styles.form} onSubmit={handleCredentialsSubmit}>
                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel} htmlFor="loginId">
                    아이디
                  </label>
                  <input
                    id="loginId"
                    type="text"
                    className={styles.input}
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    placeholder="아이디를 입력하세요"
                    disabled={isSubmitting}
                    autoComplete="username"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel} htmlFor="password">
                    비밀번호
                  </label>
                  <input
                    id="password"
                    type="password"
                    className={styles.input}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호를 입력하세요"
                    disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                </div>

                {error && <div className={styles.error}>{error}</div>}
                {regMessage && <div className={styles.success}>{regMessage}</div>}

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting || !loginId.trim() || !password.trim()}
                >
                  {isSubmitting ? '로그인 중...' : '로그인'}
                </button>

                <div className={styles.registerLink}>
                  아직 계정이 없으신가요?{' '}
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => { setShowRegister(true); setError(null); setRegMessage(null); }}
                  >
                    계정 설정하기
                  </button>
                </div>
                <div className={styles.registerLink}>
                  비밀번호를 잊으셨나요?{' '}
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => { setShowRecovery(true); setError(null); }}
                  >
                    비밀번호 재설정
                  </button>
                </div>
              </form>
            ) : (
              <form className={styles.form} onSubmit={handleLicenseKeySubmit}>
                <LicenseKeyInput
                  value={licenseKey}
                  onChange={setLicenseKey}
                  disabled={isSubmitting}
                />

                {error && <div className={styles.error}>{error}</div>}

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting || !licenseKey.trim()}
                >
                  {isSubmitting ? '확인 중...' : '로그인'}
                </button>
              </form>
            )}
          </>
        ) : (
          /* 계정 설정 화면 */
          <form className={styles.form} onSubmit={handleRegister}>
            <h2 className={styles.registerTitle}>계정 설정</h2>
            <p className={styles.registerDesc}>
              라이선스 키로 인증 후 아이디와 비밀번호를 설정합니다.
            </p>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel} htmlFor="regLicenseKey">
                라이선스 키
              </label>
              <input
                id="regLicenseKey"
                type="text"
                className={styles.input}
                value={regLicenseKey}
                onChange={(e) => setRegLicenseKey(e.target.value)}
                placeholder="WAIT-XXXX-XXXX-XXXX-XXXX"
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel} htmlFor="regLoginId">
                아이디
              </label>
              <input
                id="regLoginId"
                type="text"
                className={styles.input}
                value={regLoginId}
                onChange={(e) => setRegLoginId(e.target.value)}
                placeholder="4자 이상"
                disabled={isSubmitting}
                autoComplete="username"
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel} htmlFor="regPassword">
                비밀번호
              </label>
              <input
                id="regPassword"
                type="password"
                className={styles.input}
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                placeholder="6자 이상"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel} htmlFor="regPasswordConfirm">
                비밀번호 확인
              </label>
              <input
                id="regPasswordConfirm"
                type="password"
                className={styles.input}
                value={regPasswordConfirm}
                onChange={(e) => setRegPasswordConfirm(e.target.value)}
                placeholder="비밀번호를 다시 입력하세요"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? '설정 중...' : '설정 완료'}
            </button>

            <div className={styles.registerLink}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => { setShowRegister(false); setError(null); }}
              >
                로그인으로 돌아가기
              </button>
            </div>
          </form>
        )}
      </div>

      {showRecovery && (
        <PasswordRecoveryModal onClose={() => setShowRecovery(false)} />
      )}
    </div>
  );
}
